import { createInputTokens } from '../tokens/components.js';
import { SEMANTIC, type SemanticTokens } from '../tokens/semantic.js';

/**
 * Inputgroup Component Configuration
 *
 * Addon specs (prefix/suffix):
 * - Background: matches input (white/ground)
 * - Border: border-default on connecting side
 * - Text color: text-muted (#394661)
 * - Padding: 15px 16px, the input's own, so a field with a unit stays 56px tall like any other
 */
export function createInputgroup(s: SemanticTokens) {
  const inputTokens = createInputTokens(s);
  return {
    addon: {
      background: inputTokens.bg,
      borderColor: inputTokens.border,
      color: inputTokens.placeholder,
      borderRadius: inputTokens.base.radius,
      padding: `${inputTokens.base.paddingY} ${inputTokens.base.paddingX}`,
      minWidth: 'auto',
    },
    colorScheme: {
      light: {
        addon: {
          background: inputTokens.bg,
          borderColor: inputTokens.border,
          color: inputTokens.addon,
        },
      },
    },
    css: () => `
    /* Input group container */
    .p-inputgroup {
      border-radius: ${inputTokens.base.radius};
      overflow: hidden;
    }
    
    /* Remove all border radius from children inside input group */
    .p-inputgroup > .p-inputgroupaddon,
    .p-inputgroup > .p-inputtext,
    .p-inputgroup > .p-component,
    .p-inputgroup > .p-select,
    .p-inputgroup > .p-datepicker {
      border-radius: 0;
    }

    /* nbx-*-field wrappers use :host{display:contents}, so their real bordered p-* control sits one
       level deeper than a direct child. Square every corner of that control first, then (below) round
       only the group's outer edge on it. overflow:hidden alone can't round these — it would clip the
       square corner's border and leave a transparent notch. */
    .p-inputgroup > * > .p-inputnumber,
    .p-inputgroup > * > .p-select,
    .p-inputgroup > * > .p-datepicker,
    .p-inputgroup > * > .p-inputnumber .p-inputtext,
    .p-inputgroup > * > .p-datepicker .p-inputtext,
    .p-inputgroup > * > .p-inputtext {
      border-radius: 0;
    }

    /* First child - round left corners (direct child, its .p-component, and one-level-deeper wrapped controls) */
    .p-inputgroup > :first-child,
    .p-inputgroup > :first-child > .p-component,
    .p-inputgroup > :first-child > .p-select,
    .p-inputgroup > :first-child > .p-inputtext,
    .p-inputgroup > :first-child > .p-inputnumber .p-inputtext,
    .p-inputgroup > :first-child > .p-datepicker .p-inputtext {
      border-top-left-radius: ${inputTokens.base.radius};
      border-bottom-left-radius: ${inputTokens.base.radius};
    }

    /* Last child - round right corners (direct child, its .p-component, and one-level-deeper wrapped controls) */
    .p-inputgroup > :last-child,
    .p-inputgroup > :last-child > .p-component,
    .p-inputgroup > :last-child > .p-select,
    .p-inputgroup > :last-child > .p-inputtext,
    .p-inputgroup > :last-child > .p-inputnumber .p-inputtext,
    .p-inputgroup > :last-child > .p-datepicker .p-inputtext {
      border-top-right-radius: ${inputTokens.base.radius};
      border-bottom-right-radius: ${inputTokens.base.radius};
    }
    
    /* Remove double borders between elements */
    .p-inputgroup > .p-inputtext:not(:first-child),
    .p-inputgroup > .p-inputgroupaddon:not(:first-child),
    .p-inputgroup > .p-component:not(:first-child) {
      margin-left: -1px;
    }

    /* A p-select rendered inside a group addon (e.g. the phone prefix) must be borderless and
       transparent so the addon is the single bordered box — its styleClass does not reliably reach
       the p-select root in PrimeNG 22, which otherwise renders a second border. */
    .p-inputgroup > .p-inputgroupaddon .p-select {
      border: 0;
      box-shadow: none;
      background: transparent;
      border-radius: 0;
    }

    /* Error state: addon border reflects invalid input */
    .p-inputgroup:has(.p-inputtext.ng-invalid.ng-dirty) > .p-inputgroupaddon,
    .p-inputgroup:has(.p-inputtext.p-invalid) > .p-inputgroupaddon,
    .p-inputgroup:has(.p-inputtext[invalid="true"]) > .p-inputgroupaddon {
      border-color: ${inputTokens.borderInvalid};
    }
  `,
  };
}
export const inputgroup = createInputgroup(SEMANTIC);
